import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

interface Period {
  period_id: number;
  period_name: string;
  period_start: string;
  period_end: string;
  fiscal_year: number;
  status: 'open' | 'closed' | 'locked';
  closed_at?: string;
  closed_by?: number;
}

const AccountingPeriods: React.FC = () => {
  const [periods, setPeriods] = useState<Period[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPeriods();
  }, []);

  const loadPeriods = async () => {
    try {
      setLoading(true);
      // This endpoint may need to be added to your backend
      // TODO: Replace with actual periods endpoint when backend is ready
      // await accountingService.getAccounts(); // Placeholder
      setPeriods([]);
    } catch (err) {
      console.error('Error loading periods:', err);
      setError('Failed to load accounting periods');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return '#10b981';
      case 'closed': return '#f59e0b';
      case 'locked': return '#ef4444';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Loading accounting periods...</LoadingMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <div>
          <BackLink to="/accounting">← Back to Accounting</BackLink>
          <h1>Accounting Periods</h1>
          <Subtitle>Control which periods are open for journal entry posting</Subtitle>
        </div>
      </Header>

      <InfoBox>
        <strong>Open</strong> periods accept new journal entries. <strong>Closed</strong> periods are locked for posting but can be reopened by an admin. <strong>Locked</strong> periods are permanently closed for year-end.
      </InfoBox>

      {error && <ErrorAlert>{error}</ErrorAlert>}

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <Th>Period</Th>
              <Th>Start Date</Th>
              <Th>End Date</Th>
              <Th>Fiscal Year</Th>
              <Th>Status</Th>
              <Th>Closed At</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {periods.length === 0 ? (
              <tr>
                <Td colSpan={7}>
                  <EmptyState>
                    No accounting periods found. Periods are created automatically when you post journal entries.
                  </EmptyState>
                </Td>
              </tr>
            ) : (
              periods.map((period) => (
                <Tr key={period.period_id}>
                  <Td><strong>{period.period_name}</strong></Td>
                  <Td>{new Date(period.period_start).toLocaleDateString()}</Td>
                  <Td>{new Date(period.period_end).toLocaleDateString()}</Td>
                  <Td>{period.fiscal_year}</Td>
                  <Td>
                    <StatusBadge $color={getStatusColor(period.status)}>
                      {period.status.toUpperCase()}
                    </StatusBadge>
                  </Td>
                  <Td>{period.closed_at ? new Date(period.closed_at).toLocaleDateString() : '—'}</Td>
                  <Td>
                    {period.status === 'open' ? (
                      <ActionButton $type="warning">Close Period</ActionButton>
                    ) : period.status === 'closed' ? (
                      <ActionButton $type="success">Reopen</ActionButton>
                    ) : (
                      <span style={{ color: '#999', fontSize: '12px' }}>Locked</span>
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </tbody>
        </Table>
      </TableContainer>
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 30px;

  h1 {
    font-size: 28px;
    color: #333;
    margin: 10px 0;
  }
`;

const BackLink = styled(Link)`
  color: #667eea;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 14px;
`;

const InfoBox = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  font-size: 14px;
  color: #1e40af;
  line-height: 1.6;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const ErrorAlert = styled.div`
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  color: #991b1b;
  font-size: 14px;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 16px;
  font-size: 13px;
  font-weight: 600;
  color: #666;
  background: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
`;

const Tr = styled.tr`
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }

  &:not(:last-child) td {
    border-bottom: 1px solid #f3f4f6;
  }
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #333;
`;

const StatusBadge = styled.span<{ $color: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: white;
  background: ${props => props.$color};
`;

const ActionButton = styled.button<{ $type: 'success' | 'warning' }>`
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  background: ${props => props.$type === 'success' ? '#10b981' : '#f59e0b'};
  color: white;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-size: 14px;
`;

export default AccountingPeriods;
